import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useSeo } from "@/seo";
import { PlayerLine, Reveal, mono } from "@/components/home/Primitives";

function fmt(iso, lang) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export default function WeekPage() {
  const { week } = useParams();
  const { t, lang } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(week ? `/week/${week}` : "/week").then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [week]);
  const title = d && !d.error ? t("week.title", { start: fmt(d.start, lang), end: fmt(d.end, lang) }) : t("week.title_short");
  useSeo(title, d && !d.error ? t("week.desc", d.stats) : "", week ? `/week/${week}` : "/week");
  if (!d) return <p className="pt-10 wl-mono text-[10px] uppercase text-slate-500">{t("common.loading")}</p>;
  if (d.error) return <p className="pt-10 text-sm text-slate-400" data-testid="week-error">{t("week.invalid")}</p>;
  const nav = "inline-flex items-center gap-1 wl-mono text-[10px] uppercase tracking-[.04em] text-[var(--wl-accent)] hover:underline";
  return (
    <div className="wl-scope pt-6" data-testid="week-page" data-week={d.week}>
      <p className={mono}>{t("week.kicker")} · {d.week}{d.is_current ? ` · ${t("week.current")}` : ""}</p>
      <h1 className="mt-1 font-sg text-3xl font-bold uppercase tracking-[-0.04em] text-[var(--wl-text)] sm:text-5xl" data-testid="week-title">{title}</h1>
      <div className="mt-4 grid grid-cols-3 gap-4" data-testid="week-stats">
        {[["milestones", d.stats.milestones], ["players", d.stats.players], ["leagues", d.stats.leagues]].map(([k, v]) => (
          <div key={k}><p className="font-sg text-3xl font-bold tabular-nums text-[var(--wl-text)]" data-testid={`week-stat-${k}`}>{v}</p><p className={mono}>{t(`week.stat_${k}`)}</p></div>
        ))}
      </div>
      {d.groups.length === 0 ? (
        <p className="mt-8 text-sm text-[var(--wl-muted)]" data-testid="week-empty">{t("week.empty")}</p>
      ) : d.groups.map((g, i) => (
        <Reveal key={g.league_name} as="section" delay={i * 60} className="mt-8" testid={`week-group-${i}`}>
          <h2 className="mb-1 font-sg text-lg font-bold uppercase tracking-[-0.035em] text-[var(--wl-text)]">{g.league_name} <span className={mono}>{g.items.length}</span></h2>
          {g.items.map((r, j) => <PlayerLine key={`${r.player_id}-${r.type}-${j}`} r={r} t={t} lang={lang} right={r.date} testid={`week-row-${r.player_id}-${r.type}`} />)}
        </Reveal>
      ))}
      <div className="mt-10 flex items-center justify-between" data-testid="week-nav">
        {d.prev ? <Link to={`/week/${d.prev}`} className={nav} data-testid="week-prev"><ArrowLeft className="h-3 w-3" /> {t("week.prev")}</Link> : <span />}
        {d.next ? <Link to={d.next === undefined ? "/week" : `/week/${d.next}`} className={nav} data-testid="week-next">{t("week.next")} <ArrowRight className="h-3 w-3" /></Link> : <span />}
      </div>
    </div>
  );
}
